import {
  AgregadoDeInventarioSchema,
  BigBagResumenSchema,
  ComparativoSchema,
  ExportacionesDelComparativoSchema,
  type TipoDeComparativo,
  ConsultaAgregadoSchema,
  ConsultaInventarioSchema,
  FiltrosDeConsultaSchema,
  FiltrosDeInventarioSchema,
  OpcionesDeColumnaSchema,
  PaginaDeInventarioSchema,
  ResultadoDeCambioDeExportacionSchema,
  ResultadoDeEdicionDeBigBagSchema,
  ResultadoDeEliminarBigBagsSchema,
  ResultadoDeEntradaABodegaSchema,
  ResultadoDeLoteDeBigBagsSchema,
  type LoteDeBigBags,
  type CambioDeExportacionDeBolsas,
  type EdicionDeBigBag,
  type EntradaABodegaPorRecoleccion,
  ResumenDeInventarioSchema,
  type ConsultaAgregado,
  type ConsultaInventario,
  type EliminarBigBags,
  type FiltrosDeConsulta,
} from '@cnc/contracts';
import {keepPreviousData, useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {pedir} from './cliente';
import {aParametros, desdeParametros} from './consulta';
import {refrescarDatos} from './refrescar';

export const consultaDesdeParametros = (parametros: URLSearchParams) =>
  desdeParametros(parametros, ConsultaInventarioSchema);
export const consultaDeReporteDesdeParametros = (parametros: URLSearchParams) =>
  desdeParametros(parametros, ConsultaAgregadoSchema);

const POR_DEFECTO: Record<string, unknown> = {
  ...ConsultaInventarioSchema.parse({}),
  ...ConsultaAgregadoSchema.parse({}),
};

export const parametrosDesdeConsulta = (consulta: Record<string, unknown>) => aParametros(consulta, POR_DEFECTO);

// Solo los filtros (sin paginacion ni dimensiones), para las listas encadenadas.
export function soloFiltros(consulta: Record<string, unknown>): FiltrosDeConsulta {
  return FiltrosDeConsultaSchema.parse(consulta);
}

// Cambiar (o quitar) la exportacion de Big Bags elegidos: revisar o aplicar.
export function useCambiarExportacionDeBolsas() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (cuerpo: CambioDeExportacionDeBolsas) =>
      pedir('inventario/big-bags/exportacion', ResultadoDeCambioDeExportacionSchema, {
        method: 'POST',
        headers: {'content-type': 'application/json'},
        body: JSON.stringify(cuerpo),
      }),
    onSuccess: async (_resultado, cuerpo) => {
      if (cuerpo.modo === 'aplicar') await refrescarDatos(cliente);
    },
  });
}

// Eliminar Big Bags elegidos o todos los filtrados (solo administradores).
export function useEliminarBigBags() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (cuerpo: EliminarBigBags) =>
      pedir('inventario/big-bags/eliminar', ResultadoDeEliminarBigBagsSchema, {
        method: 'POST',
        headers: {'content-type': 'application/json'},
        body: JSON.stringify(cuerpo),
      }),
    onSuccess: async (_resultado, cuerpo) => {
      if (cuerpo.modo === 'eliminar') await refrescarDatos(cliente);
    },
  });
}

export function useInventario(consulta: ConsultaInventario) {
  const cadena = parametrosDesdeConsulta(consulta).toString();
  return useQuery({
    queryKey: ['inventario', 'lista', cadena],
    queryFn: () => pedir(`inventario/big-bags${cadena ? `?${cadena}` : ''}`, PaginaDeInventarioSchema),
    placeholderData: keepPreviousData,
  });
}

export function useFiltrosDeInventario(filtros: FiltrosDeConsulta) {
  const cadena = parametrosDesdeConsulta(filtros).toString();
  return useQuery({
    queryKey: ['inventario', 'filtros', cadena],
    queryFn: () => pedir(`inventario/filtros${cadena ? `?${cadena}` : ''}`, FiltrosDeInventarioSchema),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

// Valores de una columna para su filtro por columna; solo se pide al abrir la lista.
export function useOpcionesDeColumnaDeInventario(
  filtros: FiltrosDeConsulta,
  columna: string,
  contiene: string,
  habilitado: boolean,
) {
  const parametros = parametrosDesdeConsulta(filtros);
  parametros.set('columna', columna);
  if (contiene) parametros.set('contiene', contiene);
  const cadena = parametros.toString();
  return useQuery({
    queryKey: ['inventario', 'opciones-de-columna', cadena],
    queryFn: () => pedir(`inventario/opciones-de-columna?${cadena}`, OpcionesDeColumnaSchema),
    enabled: habilitado,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

export function useAgregado(consulta: ConsultaAgregado) {
  const cadena = parametrosDesdeConsulta(consulta).toString();
  return useQuery({
    queryKey: ['inventario', 'agregado', cadena],
    queryFn: () => pedir(`inventario/agregado${cadena ? `?${cadena}` : ''}`, AgregadoDeInventarioSchema),
    placeholderData: keepPreviousData,
  });
}

export function urlDeExcelDelReporte(consulta: ConsultaAgregado) {
  const cadena = parametrosDesdeConsulta(consulta).toString();
  return `/api/inventario/agregado/excel${cadena ? `?${cadena}` : ''}`;
}

// Filtros del comparativo del inicio (exportador, proveedor y mina: varios valores con "|").
export type FiltrosDelComparativo = {
  anio?: number;
  // Año contra el que se compara; sin el, el anterior.
  anioComparar?: number;
  exportador?: string;
  proveedor?: string;
  mina?: string;
  tipo: TipoDeComparativo;
  // Meses del año (1 a 12) separados por "|".
  mesesEtd?: string;
  // Horizonte de la proyeccion (solo cuando se aprieta el boton).
  proyeccion?: '3' | '6' | '9' | '12';
};
const parametrosDelComparativo = (consulta: FiltrosDelComparativo) => {
  const parametros = new URLSearchParams({tipo: consulta.tipo});
  if (consulta.anio) parametros.set('anio', String(consulta.anio));
  if (consulta.anioComparar) parametros.set('anioComparar', String(consulta.anioComparar));
  if (consulta.exportador) parametros.set('exportador', consulta.exportador);
  if (consulta.proveedor) parametros.set('proveedor', consulta.proveedor);
  if (consulta.mina) parametros.set('mina', consulta.mina);
  if (consulta.mesesEtd) parametros.set('mesesEtd', consulta.mesesEtd);
  if (consulta.proyeccion) parametros.set('proyeccion', consulta.proyeccion);
  return parametros;
};

// Año contra año del tablero de inicio.
export function useComparativo(consulta: FiltrosDelComparativo) {
  const parametros = parametrosDelComparativo(consulta);
  return useQuery({
    queryKey: ['inventario', 'comparativo', parametros.toString()],
    queryFn: () => pedir(`inventario/comparativo?${parametros.toString()}`, ComparativoSchema),
    placeholderData: keepPreviousData,
    refetchInterval: 5 * 60_000,
  });
}

// Las exportaciones de la grafica del comparativo: las de los meses elegidos
// ("2026-03|2025-07") o, sin meses, las de todo el periodo.
export function useExportacionesDelComparativo(consulta: FiltrosDelComparativo & {meses?: string}) {
  const parametros = parametrosDelComparativo(consulta);
  if (consulta.meses) parametros.set('meses', consulta.meses);
  return useQuery({
    queryKey: ['inventario', 'comparativo', 'exportaciones', parametros.toString()],
    queryFn: () =>
      pedir(`inventario/comparativo/exportaciones?${parametros.toString()}`, ExportacionesDelComparativoSchema),
    placeholderData: keepPreviousData,
  });
}

export function useResumenDeInventario(activo = true) {
  return useQuery({
    enabled: activo,
    queryKey: ['inventario', 'resumen'],
    queryFn: () => pedir('inventario/resumen', ResumenDeInventarioSchema),
    refetchInterval: 60_000,
  });
}

export function useBigBag(bbNo: string) {
  return useQuery({
    queryKey: ['inventario', 'big-bag', bbNo],
    queryFn: () => pedir(`inventario/big-bags/${encodeURIComponent(bbNo)}`, BigBagResumenSchema),
    retry: false,
  });
}

// Fecha de ingreso a bodega de los Big Bags de una recoleccion (revisar / aplicar).
export function useEntradaABodega() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (cuerpo: EntradaABodegaPorRecoleccion) =>
      pedir('inventario/recolecciones/entrada-bodega', ResultadoDeEntradaABodegaSchema, {
        method: 'POST',
        headers: {'content-type': 'application/json'},
        body: JSON.stringify(cuerpo),
      }),
    onSuccess: async (_resultado, cuerpo) => {
      if (cuerpo.modo === 'aplicar') await refrescarDatos(cliente);
    },
  });
}

// Edicion rapida de la tabla: un campo de cualquier Big Bag, con la version de la fila.
export function useEditarCeldaDeBigBag() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: ({bbNo, edicion}: {bbNo: string; edicion: EdicionDeBigBag}) =>
      pedir(`inventario/big-bags/${encodeURIComponent(bbNo)}`, ResultadoDeEdicionDeBigBagSchema, {
        method: 'PATCH',
        headers: {'content-type': 'application/json'},
        body: JSON.stringify(edicion),
      }),
    onSuccess: () => refrescarDatos(cliente),
    // Un choque (otra persona cambio la bolsa) recarga la tabla con lo de ahora.
    onError: () => refrescarDatos(cliente),
  });
}

// Cambio en lote: el mismo dato en varios Big Bags ("revisar" o "aplicar").
export function useCambioEnLoteDeBigBags() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (cuerpo: LoteDeBigBags) =>
      pedir('inventario/big-bags/lote', ResultadoDeLoteDeBigBagsSchema, {
        method: 'POST',
        headers: {'content-type': 'application/json'},
        body: JSON.stringify(cuerpo),
      }),
    onSuccess: async resultado => {
      if (resultado.aplicado) await refrescarDatos(cliente);
    },
  });
}

// Editar un Big Bag: solo los campos que cambian, con la version que se leyo.
export function useEditarBigBag(bbNo: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (edicion: EdicionDeBigBag) =>
      pedir(`inventario/big-bags/${encodeURIComponent(bbNo)}`, ResultadoDeEdicionDeBigBagSchema, {
        method: 'PATCH',
        headers: {'content-type': 'application/json'},
        body: JSON.stringify(edicion),
      }),
    onSuccess: () => refrescarDatos(cliente),
  });
}
